<i18n lang="yaml" locale="en">
  choose format: Choose format
  portrait: Portrait
  landscape: Landscape
  copyright: Copyright ©2020 touchwert®<br>touchwert® is a registered trademark of bestzoom GmbH<br>touchwert creator V2.0
</i18n>

<i18n lang="yaml" locale="de">
  choose format: Format wählen
  portrait: Hochformat (9:16)
  landscape: Querformat (16:9)
  copyright: Copyright ©2020 touchwert®<br>touchwert® ist eine eingetragene Marke der bestzoom GmbH<br>touchwert creator V2.0
</i18n>

<template>
  <div
    :class="classes"
    :key="format"
  >
    <component
      v-for="child in children"
      :key="child.getters.path"
      :path="child.getters.path"
      :is="child.getters.type"
      v-bind="child.getters.props"
    />
    <keep-alive>
      <project-editor
        v-if="$editMode"
        ref="editor"
      />
    </keep-alive>
    <keep-alive>
      <prop-editor v-if="$editMode" />
    </keep-alive>
  </div>
</template>

<script>
import BaseModule from '@/components/modules/BaseModule'
import ProjectEditor from '@/components/editor/ProjectEditor'
import PropEditor from '@/components/editor/PropEditor'
import EventBus from '@/event-bus/eventBus'
import { DuplicateLocale } from '@/errors'

export default {
  // eslint-disable-next-line vue/name-property-casing
  name: 'project',
  extends: BaseModule,
  components: {
    ProjectEditor,
    PropEditor
  },
  provide () {
    return {
      $project: this
    }
  },
  customStore: {
    getters: {
      presetColors: (state, getters) => {
        return getters.props._presetColors || []
      }
    },
    actions: {
      addPresetColor: ({ commit, getters }, { color }) => {
        const colors = getters.props._presetColors || []
        const existsIndex = colors.indexOf(color)
        if (existsIndex > -1) {
          colors.splice(existsIndex, 1)
        }
        colors.push(color)
        if (colors.length > 6) {
          colors.shift()
        }
        commit('setProp', { key: '_presetColors', value: colors })
      },
      addLocale ({ dispatch, getters }, { locale }) {
        if (getters.locales.includes(locale) || getters.defaultLocale === locale) {
          throw new DuplicateLocale(locale)
        }
        dispatch('setProp', { key: '_locales', value: [...getters.locales, locale] })
      },
      setDefaultLocale ({ dispatch, getters }, { locale }) {
        dispatch('setProp', { key: '_defaultLocale', value: locale })
      },
      removeLocale ({ dispatch, getters }, { locale }) {
        dispatch('setProp', { key: '_locales', value: getters.locales.filter(loc => loc !== locale) })
      },
      resetLocales ({ dispatch, getters }) {
        dispatch('setProp', { key: '_locales', value: [] })
        dispatch('setProp', { key: '_defaultLocale', value: '' })
      }
    }
  },
  props: {
    name: {
      type: String,
      default: 'New Project'
    },
    // eslint-disable-next-line
    _headlineFormat: {
      input: 'heading',
      label: 'choose format'
    },
    format: {
      type: String,
      input: 'radio',
      label: 'choose format',
      default: 'landscape',
      choices: {
        landscape: 'landscape',
        portrait: 'portrait'
      },
      validator (value) {
        return ['portrait', 'landscape'].includes(value)
      }
    },
    // eslint-disable-next-line
    _helpCopyright: {
      input: 'help',
      label: 'copyright'
    }
  },
  computed: {
    classes () {
      return {
        [this.format]: true
      }
    }
  },
  methods: {
    hexToRgb (hex) {
      var c
      if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
        c = hex.substring(1).split('')
        if (c.length === 3) {
          c = [c[0], c[0], c[1], c[1], c[2], c[2]]
        }
        c = '0x' + c.join('')
        return [(c >> 16) & 255, (c >> 8) & 255, c & 255].join(',')
      }
      throw new Error('Bad Hex')
    },
    onMediaIsPlaying (event) {
      this.$root.$store.commit('setMediaState', {
        path: event.path,
        playingState: !event.muted
      })
    },
    onMediaAutoplayRequest (event) {
      if (event.muted) {
        this.emitMediaIsPlaying(event.path, true)
      } else if (this.$root.$store.getters.mediaPlayingCount === 0) {
        this.emitMediaIsPlaying(event.path, false)
      }
    },
    onMediaIsStopped (event) {
      this.$root.$store.commit('setMediaState', {
        path: event.path,
        playingState: undefined
      })
    },
    emitMediaIsPlaying (path, muted) {
      EventBus.$emit('media-is-playing', { path, muted })
    }
  },
  watch: {
    isTarget: {
      immediate: true,
      handler (isTarget) {
        if (isTarget) {
          const slider = this.$store.getters.childrenByName.slider
          if (slider) {
            this.$router.replace(slider.getters.path)
          }
        }
      }
    }
  },
  mounted () {
    const smoothSlider = this.$store.getters.childrenByName.smoothSlider
    if (smoothSlider) {
      this.$router.push(smoothSlider.getters.path)
    }

    EventBus.$on('media-is-playing', this.onMediaIsPlaying)
    EventBus.$on('media-autoplay-request', this.onMediaAutoplayRequest)
    EventBus.$on('media-is-stopped', this.onMediaIsStopped)
    this.$emit('mounted')
  },
  destroyed () {
    EventBus.$off('media-is-playing', this.onMediaIsPlaying)
    EventBus.$off('media-autoplay-request', this.onMediaAutoplayRequest)
    EventBus.$off('media-is-stopped', this.onMediaIsStopped)
  }
}
</script>

<style lang="scss">
@import '~@/animations';

.landscape {
  font-size: 1.5vh;
  line-height: 1.5;
}

.portrait {
  font-size: 0.85vh;
  line-height: 1.5;
}
</style>
